/**
 * The new Yanyu, plan B "เมืองสองฝั่งน้ำ" (the user's pick, 2026-10-07; docs/archive/DIRECTION.md), as a walkable greybox
 * zone: the plan's data (yanyu2Plans.ts `YANYU2_PLANS` B) turned into a layout (layout.ts builds it) and its zone.
 *
 * - 600 × 600 m, flat ground at 0; the demo plaza (demoPlaza.ts, copied whole) at x/y 260–340 with gates opened in
 *   its north, east and west walls; the river (water) past its east side to the sea in the south.
 * - Raised ground is hard (walls at its edges, never graded): the foothill shelves (15 / 20 m), the schools' crags
 *   (55–100 m, `cragStairs`), the bandits' hill (7 m). Ramps and stairs climb them; everything else is qinggong.
 * - The plan's districts become paving with house lots, its forests and red rocks scattered trees and boulders.
 *
 * The client's ko view draws the same map as a greybox (apps/client/src/render3d2/koYanyu2.ts), with the map
 * (x, y) at ko scene (x − 300, y − 300). Hooked up by docs/handoff/newdir/N1-hooks.patch (the zone id, the zone, the
 * layout's registration, `?map=yanyu2`).
 */
import type { FlightOp, LayoutBuilding, LayoutOp, LayoutSpec, PaintOp, Rect, XY } from '../../layout';
import type { ZoneDef, ZoneId } from '../zones';
import { DEMO_PLAZA_LAYOUT } from './demoPlaza';
import { DUMMY_YARDS } from '../dummies';
import { DUNGEON_ENTRANCE } from '../dungeon';
import { cragStairs, type GreyArea, type GreyPlan, type Pt, YANYU2_PLANS } from './yanyu2Plans';

/** The zone's id (added to `ZoneId` by the hook patch; until then a cast). */
export const YANYU2_ZONE_ID = 'yanyu2' as ZoneId;
/** The ko scene's origin on this map: the plaza's middle. */
export const YANYU2_ORIGIN = 300;

const PLAN: GreyPlan = YANYU2_PLANS.find((p) => p.id === 'B')!;
const S = PLAN.w;
const [PX, PY] = PLAN.plaza;
const P = DEMO_PLAZA_LAYOUT.w;

const rectPoly = (r: Rect): XY[] => [
  [r[0], r[2]],
  [r[1], r[2]],
  [r[1], r[3]],
  [r[0], r[3]],
];
function inPoly(p: readonly (XY | Pt)[], x: number, y: number): boolean {
  let inside = false;
  for (let a = 0, b = p.length - 1; a < p.length; b = a++) {
    const [xa, ya] = p[a]!;
    const [xb, yb] = p[b]!;
    if (ya > y !== yb > y && x < ((xb - xa) * (y - ya)) / (yb - ya) + xa) inside = !inside;
  }
  return inside;
}
function segDist(px: number, py: number, a: Pt, b: Pt): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const t = Math.max(0, Math.min(1, ((px - a[0]) * dx + (py - a[1]) * dy) / (dx * dx + dy * dy || 1)));
  return Math.hypot(px - a[0] - t * dx, py - a[1] - t * dy);
}
/** The plan renderer's hash (tools/yanyu2plan), so the lots left out are the same ones. */
const hash = (a: number, b: number, c = 0) => {
  let h = (a * 374761393 + b * 668265263 + c * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};

/** A polygon as one-metre rows of rects (layout shapes have no polygon). */
function polyRows(poly: readonly Pt[]): Rect[] {
  const out: Rect[] = [];
  const ys = poly.map((p) => p[1]);
  for (let j = Math.max(0, Math.floor(Math.min(...ys))); j < Math.min(S, Math.ceil(Math.max(...ys))); j++) {
    const y = j + 0.5;
    const xs: number[] = [];
    for (let a = 0, b = poly.length - 1; a < poly.length; b = a++) {
      const [xa, ya] = poly[a]!;
      const [xb, yb] = poly[b]!;
      if (ya > y !== yb > y) xs.push(xa + ((y - ya) * (xb - xa)) / (yb - ya));
    }
    xs.sort((p, q) => p - q);
    for (let k = 0; k + 1 < xs.length; k += 2) {
      const x0 = Math.max(0, Math.round(xs[k]!));
      const x1 = Math.min(S, Math.round(xs[k + 1]!));
      if (x1 > x0) out.push([x0, x1, j, j + 1]);
    }
  }
  return out;
}

// The raised ground: the plan's terraces and island, its crags and their landings.
const crags = PLAN.crags ?? [];
const stairs = crags.map(cragStairs);
const raised: { poly: readonly (XY | Pt)[]; level: number; area: GreyArea | null }[] = [
  ...PLAN.areas
    .filter((a) => a.level && (a.kind === 'terrace' || a.kind === 'cliff' || a.kind === 'island'))
    .map((a) => ({ poly: a.poly ?? rectPoly(a.rect!), level: a.level!, area: a })),
  ...crags.map((c) => ({ poly: rectPoly(c.rect), level: c.level, area: null })),
  ...stairs.flatMap((s) => s.pads.map((p) => ({ poly: rectPoly(p.rect), level: p.level, area: null }))),
];
/** The ground's level at a point (the highest raised area over it). */
const groundAt = (x: number, y: number): number => {
  let z = 0;
  for (const r of raised) if (inPoly(r.poly, x, y)) z = Math.max(z, r.level);
  return z;
};
const plazaRect: Rect = [PX, PX + P, PY, PY + P];
const inRect = (r: Rect, x: number, y: number) => x >= r[0] && x < r[1] && y >= r[2] && y < r[3];
const nearRoad = (x: number, y: number, r: number) =>
  PLAN.roads.some((rd) => rd.pts.some((p, i) => i > 0 && segDist(x, y, rd.pts[i - 1]!, p) < r + rd.w / 2));

const ops: LayoutOp[] = [];
const buildings: LayoutBuilding[] = [];
const paint = (name: string, rect: Rect, kind: PaintOp['kind'], level: PaintOp['level'], hard = false) =>
  ops.push({ name, shape: { rect }, kind, level, ...(hard ? { hard } : {}) });

// 1. Water: the sea (deep enough to swim) and the river.
for (const a of PLAN.areas)
  if (a.kind === 'sea' || a.kind === 'river' || a.kind === 'lake')
    for (const r of polyRows(a.poly ?? rectPoly(a.rect!))) paint(a.name ?? a.kind, r, a.kind === 'sea' ? 'sea' : 'brook', 0);

// 2. The flat districts on the valley floor.
const DISTRICT: Partial<Record<GreyArea['kind'], NonNullable<PaintOp['kind']>>> = {
  market: 'pave',
  harbour: 'pave',
  homes: 'yard',
  fields: 'plot',
  training: 'yard',
  court: 'court',
};
for (const a of PLAN.areas) {
  const k = DISTRICT[a.kind];
  if (k && a.rect && !a.level) paint(a.name ?? a.kind, a.rect, k, 'ground');
}

// 2b. The rice paddies of ทุ่งนาริมน้ำ where the player farms (data/professions.ts NODE_SPOTS 'yanyu2-plot-*', lane
// ITEMS): the district paints vegetable plots, these are flooded paddy.
paint('นาข้าว', [96, 116, 282, 300], 'paddy', 'ground');

// 3. Roads on the valley floor.
const flatRoads = PLAN.roads.filter((rd) => rd.pts.every((p) => p[2] === undefined));
for (const rd of flatRoads) ops.push({ name: 'road', shape: { path: rd.pts.map((p) => [p[0], p[1]] as const), width: rd.w }, kind: 'road', level: 'ground' });

// 3b. The north road (to the region's trunk road to ลานศิลา, data/regions/jiuzhou.ts): from the core's north edge down
// past the hall's east side (between it and the west market's lots) to the plaza's north gate.
ops.push({
  name: 'north road',
  shape: { path: [[300, 0], [300, 170], [334, 170], [334, 252], [300, 252], [300, 262]] as const, width: 6 },
  kind: 'road',
  level: 'ground',
});

// 4. The raised ground, hard: shelves, the island and hills; the crags; the stairs' landings.
for (const r of raised) {
  const a = r.area;
  const kind = a?.kind === 'island' ? 'grass' : a ? 'grass' : 'gravel';
  if (a?.poly) for (const row of polyRows(a.poly)) paint(a.name ?? 'raised', row, kind, r.level, true);
  else {
    const xs = r.poly.map((p) => p[0]);
    const ys = r.poly.map((p) => p[1]);
    paint(a?.name ?? 'crag', [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)], kind, r.level, true);
  }
}

// 5. Roads up on the raised ground (at their own level).
for (const rd of PLAN.roads.filter((r) => !flatRoads.includes(r)))
  ops.push({
    name: 'road',
    shape: { path: rd.pts.map((p) => [p[0], p[1]] as const), width: rd.w },
    kind: 'road',
    level: rd.pts[0]![2] ?? groundAt(rd.pts[0]![0], rd.pts[0]![1]),
    hard: true,
  });

// 5b. The crags and their landings once more, over the roads up on the shelves: the north-east shelf's road (x 470,
// level 20) runs on through ผาตะเกียงเหล็ก and cut a 20 m trench, walled, across its top and its school's court.
for (const r of raised.filter((r) => !r.area)) {
  const xs = r.poly.map((p) => p[0]);
  const ys = r.poly.map((p) => p[1]);
  paint('crag', [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)], 'gravel', r.level, true);
}

// 5b. The crags and their landings once more, over the roads up on the shelves: the north-east shelf's road (x 470,
// level 20) runs on through ผาตะเกียงเหล็ก and cut a 20 m trench, walled, across its top and its school's court.
for (const r of raised.filter((r) => !r.area)) {
  const xs = r.poly.map((p) => p[0]);
  const ys = r.poly.map((p) => p[1]);
  paint('crag', [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)], 'gravel', r.level, true);
}

// 6. The schools' compounds on their crags: a paved court, a wall round it with a gate in the south, three halls.
const COLOR: Record<string, LayoutBuilding['color']> = {
  'mist-peak': 'blue',
  'bell-stone': 'red',
  'lantern-road': 'red',
  'dim-moon': 'white',
  'pine-ink': 'green',
  'cinnabar': 'red',
};
for (const c of PLAN.compounds) {
  const [x0, x1, y0, y1] = c.rect;
  const z = c.base;
  paint(c.name, c.rect, 'court', z, true);
  const mx = Math.round((x0 + x1) / 2);
  for (const r of [
    [x0, x1, y0, y0 + 1],
    [x0, x0 + 1, y0, y1],
    [x1 - 1, x1, y0, y1],
    [x0, mx - 4, y1 - 1, y1],
    [mx + 4, x1, y1 - 1, y1],
  ] as Rect[])
    ops.push({ name: `${c.name} wall`, shape: { rect: r }, kind: 'wall', level: z });
  const w = x1 - x0;
  const d = y1 - y0;
  const R = (a: number, b: number, e: number, f: number): [number, number, number, number] => [
    Math.round(a),
    Math.round(b),
    Math.round(e),
    Math.round(f),
  ];
  const halls: [string, [number, number, number, number], number][] = [
    [`${c.name} หอใหญ่`, R(x0 + w * 0.22, x1 - w * 0.22, y0 + d * 0.1, y0 + d * 0.42), 9],
    [`${c.name} หอข้าง`, R(x0 + w * 0.08, x0 + w * 0.3, y0 + d * 0.5, y0 + d * 0.85), 5],
    [`${c.name} หอข้าง`, R(x1 - w * 0.3, x1 - w * 0.08, y0 + d * 0.5, y0 + d * 0.85), 5],
  ];
  for (const [name, [a, b, e, f], h] of halls)
    buildings.push({ name, kind: 'hall', x0: a, x1: b, y0: e, y1: f, base: z, eave: h, rise: 3, roof: 'hip', color: COLOR[c.school] ?? 'blue' });
}

// 7. Ramps and stairs: the plan's, and each crag's two flights.
const flights = [...(PLAN.flights ?? []), ...stairs.flatMap((s) => s.flights)];
flights.forEach((f, n) =>
  ops.push({ name: `flight ${n}`, flight: f.kind, rect: f.rect, down: f.down, bottom: f.bottom, top: f.top } satisfies FlightOp),
);

// 8. The demo plaza, whole, moved to its place; then gates through its north, east and west walls.
const shift = (r: Rect): Rect => [r[0] + PX, r[1] + PX, r[2] + PY, r[3] + PY];
for (const op of DEMO_PLAZA_LAYOUT.ops) {
  const p = op as PaintOp;
  if (!p.shape || !('rect' in p.shape)) continue;
  ops.push({ ...p, shape: { rect: shift(p.shape.rect) } });
}
const GATE = 5;
const mid = P / 2;
if (PLAN.gates.includes('n')) paint('north gate', shift([mid - GATE, mid + GATE, 0, 3]), 'court', 0);
if (PLAN.gates.includes('w')) paint('west gate', shift([0, 3, mid - GATE, mid + GATE]), 'court', 0);
if (PLAN.gates.includes('e')) paint('east gate', shift([P - 3, P, mid - GATE, mid + GATE]), 'court', 0);
// The plaza's houses with a room behind the door take the old village's ids (data/interiors.ts DOOR_LINKS).
const PLAZA_IDS: Readonly<Record<string, string>> = {
  'โรงเตี๊ยมลมทะเล': 'yanyu-inn',
  'บ้านผู้เฒ่าหลิว': 'yanyu-elder-house',
  'ร้านค้าหยานหยู': 'yanyu-shop',
  'ศาลเจ้า': 'yanyu-shrine',
  'บ้านลุงไห่': 'yanyu-home-hai',
  'บ้านชาวประมง': 'yanyu-home-2',
};
for (const b of DEMO_PLAZA_LAYOUT.buildings)
  buildings.push({
    ...b,
    ...(b.name && PLAZA_IDS[b.name] ? { id: PLAZA_IDS[b.name] } : {}),
    x0: b.x0 + PX,
    x1: b.x1 + PX,
    y0: b.y0 + PY,
    y1: b.y1 + PY,
    ...(b.door ? { door: [b.door[0] + PX, b.door[1] + PY, b.door[2]] as const } : {}),
    ...(b.passage ? { passage: [b.passage[0] + PX, b.passage[1] + PX] as const } : {}),
  });

// 8a. The training yard (ลานซ้อมหุ่นไม้, data/dummies.ts): a flat court north of the road to the duelling ground.
for (const y of DUMMY_YARDS) if (y.zone === 'yanyu2') paint(y.name, [...y.rect] as unknown as Rect, 'court', 0);

// 8b. The duelling ground (ลานประลอง, data/duelGround.ts): a walled stone ring on the east bank, east of the plaza
// bridge, with a gate in its west wall, a road to it from the east bank's road, and spectator steps (three rows of
// 0.3 m, entered at their ends) along its north, south and east sides.
export const DUEL_RING = { outer: [472, 508, 284, 316] as Rect, gate: [296, 304] as const };
{
  const [x0, x1, y0, y1] = DUEL_RING.outer;
  const [g0, g1] = DUEL_RING.gate;
  paint('road to the duelling ground', [440, x0, 297, 303], 'road', 'ground');
  paint('duelling ground', [x0 + 1, x1 - 1, y0 + 1, y1 - 1], 'court', 0);
  for (const r of [
    [x0, x1, y0, y0 + 1],
    [x0, x1, y1 - 1, y1],
    [x1 - 1, x1, y0, y1],
    [x0, x0 + 1, y0, g0],
    [x0, x0 + 1, g1, y1],
  ] as Rect[])
    ops.push({ name: 'duelling ground wall', shape: { rect: r }, kind: 'wall', level: 0 });
  paint('duelling ground gate', [x0, x0 + 1, g0, g1], 'court', 0);
  for (let k = 0; k < 3; k++) {
    const z = 0.3 * (k + 1);
    paint('spectator steps', [x0, x1, y0 - 1 - k, y0 - k], 'deck', z, true);
    paint('spectator steps', [x0, x1, y1 + k, y1 + 1 + k], 'deck', z, true);
    paint('spectator steps', [x1 + k, x1 + 1 + k, y0 - 3, y1 + 3], 'deck', z, true);
  }
}
/** Ground kept clear of house lots: the ring with its steps, and the road to it. */
const KEEP_CLEAR: Rect[] = [
  [464, 512, 279, 321],
  [440, 472, 296, 304],
];
const overlaps = (a: Rect, b: Rect) => a[0] < b[1] && b[0] < a[1] && a[2] < b[3] && b[2] < a[3];

// 8a. The lone spire (เสาหินโดดเดี่ยว) in the headwater wood: 3 x 3 m, 40 m tall, hard; the hidden master sits on top
// (data/qinggong.ts 'hidden-master', landmark `spire`). Climbed by climb.ts, or glided to from a crag.
paint('lone spire', [298.5, 301.5, 38.5, 41.5], 'gravel', 40, true);

// 8a2. The bun tower (หอไผ่) of the market fair: 4 x 4 m, 14 m (data/worldEvents.ts FAIR.tower).
paint('bun tower', [348, 352, 338, 342], 'gravel', 14, true);

// 8b. Stepping stones on the river (docs/systems/FUN_P1.md section 1): 2 m decks 0.7 m over the water, in three lines
// (north y 220, middle y 340, south y 500), 14-22 m apart so วิ่งบนน้ำ ขั้น 1 crosses with a rest on each.
export const RIVER_STONES: readonly (readonly [number, number])[] = [
  [380, 220], [398, 220], [414, 220],
  [378, 340], [396, 340], [410, 340],
  [404, 500], [424, 500], [444, 500],
];
for (const [x, y] of RIVER_STONES) paint('river stone', [x - 1, x + 1, y - 1, y + 1], 'deck', 0.7, true);

// 9. Bridges and piers (decks a step above the water), the shrine and the hall of the six schools.
for (const b of PLAN.boxes) {
  if (b.kind === 'bridge' || b.kind === 'pier') paint(b.name ?? b.kind, b.rect, 'deck', (b.base ?? 0) + b.h, true);
  else if (b.kind === 'shrine' || b.kind === 'hall') {
    const [x0, x1, y0, y1] = b.rect;
    buildings.push({
      name: b.name ?? b.kind,
      kind: 'hall',
      x0,
      x1,
      y0,
      y1,
      base: b.base ?? groundAt((x0 + x1) / 2, (y0 + y1) / 2),
      eave: b.h,
      rise: 4,
      roof: b.kind === 'shrine' ? 'tiers' : 'hip',
      color: b.kind === 'shrine' ? 'red' : 'green',
    });
  } else if (b.kind === 'wall') paint(b.name ?? 'wall', b.rect, 'wall', b.h);
}

// 9b. The cave mouth of ถ้ำคลื่นคำราม (data/dungeon.ts `DUNGEON_ENTRANCE`): a rock hut over the sea cave at the island
// shrine's south end, its door facing south onto the island; the door leads into the dungeon (data/interiors.ts).
{
  const [x0, x1, y0, y1] = DUNGEON_ENTRANCE.rect;
  const [di, dj, side] = DUNGEON_ENTRANCE.door;
  buildings.push({ id: DUNGEON_ENTRANCE.building, name: 'ปากถ้ำคลื่นคำราม', kind: 'shed', x0, x1, y0, y1, door: [di, dj, side], base: 0.6, eave: 3, rise: 1, roof: 'none', color: 'grey' });
}

// 10. The districts' house lots (as the plan renderer lays them), and the wilds' trees and boulders.
const LOT_COLOR: Partial<Record<GreyArea['kind'], LayoutBuilding['color']>> = { market: 'red', harbour: 'white', homes: 'blue' };
for (const a of PLAN.areas) {
  if (!a.lots || !a.rect) continue;
  const r = a.rect;
  const [w, d, gap, h] = a.lots;
  for (let y = r[2] + gap / 2; y + d <= r[3]; y += d + gap)
    for (let x = r[0] + gap / 2; x + w <= r[1]; x += w + gap) {
      const cx = x + w / 2;
      const cy = y + d / 2;
      if (nearRoad(cx, cy, Math.min(w, d) / 2)) continue;
      if (inRect(plazaRect, cx, cy)) continue;
      if (KEEP_CLEAR.some((k) => overlaps(k, [x, x + w, y, y + d]))) continue;
      if (hash(Math.round(x), Math.round(y), 7) < 0.12) continue;
      const g0 = groundAt(x, y);
      if ([groundAt(x + w, y), groundAt(x, y + d), groundAt(x + w, y + d)].some((v) => v !== g0)) continue;
      const [bx0, by0] = [Math.round(x), Math.round(y)];
      buildings.push({
        name: a.kind === 'market' ? 'ร้านค้า' : a.kind === 'harbour' ? 'โกดัง' : 'บ้าน',
        kind: 'house',
        x0: bx0,
        x1: bx0 + Math.round(w),
        y0: by0,
        y1: by0 + Math.round(d),
        base: g0,
        eave: Math.round(h * (0.8 + 0.6 * hash(Math.round(x), Math.round(y), 3)) * 10) / 10,
        rise: 2,
        roof: 'hip',
        color: LOT_COLOR[a.kind] ?? 'blue',
      });
    }
}
for (const a of PLAN.areas) {
  if (!a.rect || !(a.kind === 'forest' || a.kind === 'rock')) continue;
  ops.push({
    name: a.name ?? a.kind,
    scatter: { rect: a.rect, rules: [{ p: a.kind === 'forest' ? 0.05 : 0.04, kind: a.kind === 'forest' ? 'forest' : 'boulder' }] },
  });
}

const reg = (name: string): Rect => PLAN.monsters.find((m) => m.name.includes(name))!.rect;

export const YANYU2_LAYOUT: LayoutSpec = {
  id: 'yanyu2',
  zone: YANYU2_ZONE_ID,
  w: S,
  h: PLAN.h,
  seed: 602,
  keepClear: 1,
  // Flat ground at level 0 (the demo plaza's terrain, at this size): the plan's water and heights are painted.
  terrain: {
    ...DEMO_PLAZA_LAYOUT.terrain,
    coast: [
      [0, -10],
      [S, -10],
    ],
    rows: [
      [0, 0],
      [S, 0],
    ],
  },
  ops,
  buildings,
  boats: [],
  posts: [],
  // The training yard's dummies (data/dummies.ts): the props stand where the dummies do.
  props: DUMMY_YARDS.filter((y) => y.zone === 'yanyu2').flatMap((y) => y.dummies.map((d) => ({ kind: 'dummy', x: d.x, y: d.y }))),
  // Landmark ids are the old village's (scenes.ts, folk.ts, navPlaces.ts, professions.ts, animals.ts read them);
  // where each stands now is docs/archive/YANYU.md A5. x east, y south, in metres on the 600 m map.
  landmarks: {
    // C2 (PATCH1_SCRIPT): the player wakes on the river-mouth harbour's west wooden pier; Hai fishes on the next one.
    wake: [283.5, 562.5],
    pier: [283.5, 566.5],
    // The ferry pier on the river by สะพานลาน (the plaza's east gate is 25 m west), kept as its own spot.
    ferry: [366.5, 309.5],
    pierEnd: [283.5, 576.5],
    pierWest: [329.5, 566.5],
    pierEast: [503.5, 566.5],
    shore: [120.5, 556.5],
    // Homes: the west quarter, and south of the plaza.
    home1: [210.5, 290.5],
    home2: [170.5, 340.5],
    home3: [300.5, 400.5],
    houseN: [250.5, 238.5],
    houseSW: [296.5, 372.5],
    houseSE: [303.5, 372.5],
    // The island shrine: `shrine` is the island bridge's west end on the bank road (C3 triggers here).
    pavilion: [378.5, 376.5],
    shrine: [366.5, 376.5],
    // C3's trigger: the harbour road south of the plaza, 100 m west of the island (the player walks it from the pier).
    shrineRoad: [294.5, 384.5],
    shrineTop: [402.5, 370.5],
    meiPost: [402.5, 383.5],
    // The plaza's inn and the elder's house (the demo plaza's buildings).
    inn: [281.5, 271.5],
    elderHouse: [298.5, 284.5],
    // The west market, by the bridge.
    shop: [350.5, 308.5],
    guide: [346.5, 296.5],
    // The hall of the six schools, and the gate and carriage yard south of the plaza.
    schools: [300.5, 238.5],
    gate: [450.5, 450.5],
    // Chapter two's three gates (docs/DECISIONS_2026-10-08.md): the north road, the west pass, the harbour water gate.
    'gate-north': [300.5, 140.5],
    'gate-west': [135.5, 262.5],
    'gate-harbour': [305.5, 548.5],
    carriage: [300.5, 410.5],
    stationMaster: [304.5, 412.5],
    // The dummy yard (ลานซ้อมหุ่นไม้) on the east bank, and the river fields.
    trainYard: [428.5, 297.5],
    yardWallTop: [418.5, 296.5],
    fieldsWest: [80.5, 350.5],
    fieldsEast: [110.5, 400.5],
    // Under the headwater falls, the wolves' wood, the west gate's ramp, and the red rock.
    pond: [358.5, 12.5],
    grove: [308.5, 57.5],
    pass: [135.5, 252.5],
    // The wandering master of กระโจนข้ามเหว: the 15 m shelf, at the gap between the Wudang and Shaolin crags.
    // The hidden master's seat on the lone spire (40 m).
    spire: [300, 40],
    leapMaster: [136.5, 130.5],
    quarry: [552.5, 300.5],
    lair: [575.5, 360.5],
  },
  points: {
    plaza: [PX + mid, PY + mid],
    spawn: [PX + mid, PY + mid],
    elder: [PX + mid + 4, PY + mid],
    // The guard-house yard at the east bank's gate (landmark `gate`): the cell is the 4.8 m square round this point.
    jail: [452.5, 440.5],
    // Bouts (ladder, arena, challenges) in the duelling ground's ring.
    duel: [(DUEL_RING.outer[0] + DUEL_RING.outer[1]) / 2, (DUEL_RING.outer[2] + DUEL_RING.outer[3]) / 2],
    arena: [(DUEL_RING.outer[0] + DUEL_RING.outer[1]) / 2, (DUEL_RING.outer[2] + DUEL_RING.outer[3]) / 2],
    // The black sword's ring: the red rock in the east (landmark `lair`).
    lair: [575.5, 360.5],
  },
  regions: {
    wolves: reg('หมาป่า'),
    bandits: reg('โจร'),
    east: reg('ผาแดง'),
    west: [24, 136, 250, 460],
    graves: [256, 360, 10, 104],
  },
  // The warps need a zone on the world grid (zones.ts `YANYU2_ZONE.at`): south to the plains by the carriage road,
  // west to ยอดหมอก at the foot of the north-west shelf's ramp (an open question: docs/archive/YANYU.md A9).
  portals: [
    { to: 'plains' as ZoneId, side: 's', at: [300.5, 482.5] },
    { to: 'mist-peak' as ZoneId, side: 'w', at: [135.5, 238.5] },
  ],
  reserved: [],
  // The town on both banks is safe (no PK); the hills and the wilds are not.
  safe: [
    [140, 220],
    [550, 220],
    [550, 560],
    [140, 560],
  ],
};

/**
 * The zone (wild, off the world grid's links like the demo plaza): the hook patch adds it to `ZONES`. The player
 * walks at the ko crowd's gait, as in the demo plaza.
 */
export const YANYU2_ZONE: ZoneDef = {
  id: YANYU2_ZONE_ID,
  at: [0, -1],
  name: 'หมู่บ้านหยานหยู',
  kind: 'village',
  terrain: 'plains',
  size: S,
  seed: 602,
  layout: 'yanyu2',
  // แคว้นเก้ากระบี่ (data/regions/jiuzhou.ts): with region maps on the town is the core of the 3,456 m region (docs/archive/OPEN_WORLD.md Round 2).
  region: 'jiuzhou',
  music: 'yanyu',
  wildMusic: 'wilds',
  levels: 'ระดับ 2–12',
  spawns: [
    { monster: 'wolf', region: 'wolves', n: 10 },
    { monster: 'bandit', region: 'bandits', n: 10 },
    { monster: 'bear', region: 'east', n: 4 },
    // The first lesson's sparring partners (quest three-forms) wait in the river fields.
    { monster: 'sparrer', region: 'west', n: 4 },
  ],
  walkSpeed: 1.372,
};
